import { useQuery, useQueryClient } from '@tanstack/react-query';
import mqtt from 'mqtt';
import { useEffect, useRef, useState } from 'react';
import { reportChatApi } from '@/domains/traffic-monitoring/apis/report-chat';
import type {
  ChatBubbleView,
  ChatHistoryItem,
  ChatLiveMessage,
} from '@/domains/traffic-monitoring/types/report-chat.types';
import { URLS } from '@/shared/config/urls';
import { formatTimeOnly } from '@/shared/utils/date';

// First load is a REST fetch; live updates arrive over MQTT on
// report-{report_code_id} (see api-dsh's mqtt_publisher.py).

const chatTopic = (reportCodeId: string) => `report-${reportCodeId}`;

const toBubbleView = (item: ChatHistoryItem): ChatBubbleView => {
  const isOwn = item.sender_type === 'SENDER_TP_OPR';
  const time = item.registered_time ? formatTimeOnly(item.registered_time).slice(0, 5) : '';
  const isImage = item.messages_type === 'MSG_TP_IMG';
  const isVideo = item.messages_type === 'MSG_TP_VID';
  const isAudio = item.messages_type === 'MSG_TP_AUD';
  const isMedia = isImage || isVideo || isAudio;

  if (isMedia && item.media_link) {
    return {
      id: item.chat_history_id,
      image: isImage ? item.media_link : undefined,
      video: isVideo ? item.media_link : undefined,
      audio: isAudio ? item.media_link : undefined,
      caption: item.messages_text ?? undefined,
      time,
      isOwn,
    };
  }

  // media_link can lag behind the chat_history row while the file
  // downloads - show a processing placeholder instead of a blank bubble.
  if (isMedia && !item.messages_text) {
    return {
      id: item.chat_history_id,
      isProcessing: true,
      time,
      isOwn,
    };
  }

  return {
    id: item.chat_history_id,
    message: item.messages_text ?? undefined,
    time,
    isOwn,
  };
};

export const useReportChat = (
  reportId?: number,
  reportCodeId?: string,
  onLiveMessage?: () => void
) => {
  const enabled = reportId != null;
  const [liveItems, setLiveItems] = useState<ChatHistoryItem[]>([]);
  const seenIds = useRef<Set<number>>(new Set());
  const queryClient = useQueryClient();
  // Ref so a caller-supplied callback never forces the MQTT client to
  // reconnect just because its function identity changed on re-render.
  const onLiveMessageRef = useRef(onLiveMessage);
  onLiveMessageRef.current = onLiveMessage;

  const {
    data: history,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['reportChat', reportId],
    queryFn: async () => {
      const res = await reportChatApi.getChatHistory(reportId as number);
      return res?.data?.chat_list ?? [];
    },
    enabled,
  });

  useEffect(() => {
    if (!history) return;
    seenIds.current = new Set(history.map((item) => item.chat_history_id));
    setLiveItems([]);
    // Loading this thread stamps reports.read_at server-side - invalidate the
    // dashboard's unread counts so navigating back doesn't show a stale number.
    queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
  }, [history, queryClient]);

  useEffect(() => {
    console.log('[chat mqtt] effect ran, reportCodeId =', reportCodeId);
    if (!reportCodeId) return;

    const topic = chatTopic(reportCodeId);
    const client = mqtt.connect(URLS.MQTT_DSH, { connectTimeout: 5000, keepalive: 15 });

    client.on('connect', () => {
      client.subscribe(topic, (err) => {
        if (err) {
          console.error('[chat mqtt] subscribe failed', topic, err);
        } else {
          console.log('[chat mqtt] subscribed', topic);
        }
      });
    });
    client.on('reconnect', () => console.log('[chat mqtt] reconnecting...', topic));
    client.on('close', () => console.log('[chat mqtt] connection closed', topic));
    client.on('error', (err) => console.error('[chat mqtt] connection error', topic, err));
    client.on('message', (receivedTopic, payload) => {
      if (receivedTopic !== topic) return;
      console.log('[chat mqtt] message received', receivedTopic, payload.toString());
      try {
        const item = JSON.parse(payload.toString()) as ChatLiveMessage;
        if (seenIds.current.has(item.chat_history_id)) return;
        seenIds.current.add(item.chat_history_id);
        setLiveItems((prev) => [...prev, item]);
        onLiveMessageRef.current?.();
      } catch (err) {
        console.error('[chat mqtt] malformed payload', payload.toString(), err);
      }
    });

    return () => {
      client.end(true);
    };
  }, [reportCodeId]);

  const chats = [...(history ?? []), ...liveItems].map(toBubbleView);

  return { chats, isLoading, isFetching, refetch };
};
